import {
  Controller,
  Get,
  Post,
  Body,
  Patch,
  Param,
  Delete,
  Query,
  UseGuards,
} from '@nestjs/common';
import {
  ApiTags,
  ApiOperation,
  ApiResponse,
  ApiBearerAuth,
  ApiQuery,
} from '@nestjs/swagger';
import { JwtAuthGuard } from '@/common/guards/jwt-auth.guard';
import { RolesGuard } from '@/common/guards/roles.guard';
import { Roles } from '@/common/guards/roles.decorator';
import { UserRole } from '@/modules/user/schemas/user.schema';
import { HeroService } from '../services/hero.service';
import { CreateHeroDto } from '../dto/create-hero.dto';
import { UpdateHeroDto } from '../dto/update-hero.dto';
import { HeroQueryDto } from '../dto/hero-query.dto';

@ApiTags('Hero')
@Controller('hero')
export class HeroController {
  constructor(private readonly heroService: HeroService) {}

  @Post()
  @UseGuards(JwtAuthGuard, RolesGuard)
  @Roles(UserRole.ADMIN)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Create a new hero section (Admin only)' })
  @ApiResponse({ status: 201, description: 'Hero created successfully' })
  create(@Body() createHeroDto: CreateHeroDto) {
    return this.heroService.create(createHeroDto);
  }

  @Get()
  @ApiOperation({ summary: 'Get all hero sections with filtering and pagination' })
  @ApiResponse({ status: 200, description: 'Heroes retrieved successfully' })
  findAll(@Query() query: HeroQueryDto) {
    return this.heroService.findAll(query);
  }

  @Get('active')
  @ApiOperation({ summary: 'Get active hero sections for frontend display' })
  @ApiResponse({ status: 200, description: 'Active heroes retrieved successfully' })
  getActiveHeroes() {
    return this.heroService.getActiveHeroes();
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get hero section by ID' })
  @ApiResponse({ status: 200, description: 'Hero retrieved successfully' })
  @ApiResponse({ status: 404, description: 'Hero not found' })
  findOne(@Param('id') id: string) {
    return this.heroService.findOne(id);
  }

  @Patch(':id')
  @UseGuards(JwtAuthGuard, RolesGuard)
  @Roles(UserRole.ADMIN)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Update hero section by ID (Admin only)' })
  @ApiResponse({ status: 200, description: 'Hero updated successfully' })
  @ApiResponse({ status: 404, description: 'Hero not found' })
  update(@Param('id') id: string, @Body() updateHeroDto: UpdateHeroDto) {
    return this.heroService.update(id, updateHeroDto);
  }

  @Delete(':id')
  @UseGuards(JwtAuthGuard, RolesGuard)
  @Roles(UserRole.ADMIN)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Delete hero section by ID (Admin only)' })
  @ApiResponse({ status: 200, description: 'Hero deleted successfully' })
  @ApiResponse({ status: 404, description: 'Hero not found' })
  remove(@Param('id') id: string) {
    return this.heroService.remove(id);
  }
} 